import { useCallback, useState, useEffect, useRef } from 'react'
import { SnackbarState, QueryMode } from './types'
import { SESSION_STORAGE_VERSIONS_KEY } from './constants'
import { useCompanyData } from './hooks/useCompanyData'
import { useQueryConfig } from './hooks/useQueryConfig'
import { useFilters } from './hooks/useFilters'
import { useResultsConsult } from './hooks/useResultsConsult'
import { useChartData } from './hooks/useChartData'
import { useDownloadReport } from './hooks/useDownloadReport'
import { useNavigation } from './hooks/useNavigation'

const useBody = () => {
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: '',
    type: 'error',
  })

  const [showVersionsPanel, setShowVersionsPanel] = useState(false)
  const [activeVersionQuestionnaireId, setActiveVersionQuestionnaireId] = useState<string | null>(null)
  const [selectedVersionsByQuestionnaire, setSelectedVersionsByQuestionnaire] = useState<Record<string, string[]>>({})

  useEffect(() => {
    const raw = sessionStorage.getItem(SESSION_STORAGE_VERSIONS_KEY)
    if (raw) {
      try {
        const saved = JSON.parse(raw)
        if (saved.selectedVersionsByQuestionnaire) setSelectedVersionsByQuestionnaire(saved.selectedVersionsByQuestionnaire)
        if (saved.showVersionsPanel !== undefined) setShowVersionsPanel(saved.showVersionsPanel)
        if (saved.activeVersionQuestionnaireId !== undefined) setActiveVersionQuestionnaireId(saved.activeVersionQuestionnaireId)
        sessionStorage.removeItem(SESSION_STORAGE_VERSIONS_KEY)
      } catch {
        sessionStorage.removeItem(SESSION_STORAGE_VERSIONS_KEY)
      }
    }
  }, [])

  const showSnackbar = useCallback((message: string, type: SnackbarState['type'] = 'error', interpolationOptions?: Record<string, unknown>) => {
    setSnackbar({ open: true, message, type, interpolationOptions })
  }, [])

  const closeSnackbar = useCallback(() => {
    setSnackbar((prev) => ({ ...prev, open: false }))
  }, [])

  const filters = useFilters()

  const companyData = useCompanyData(showSnackbar, {
    appliedPeriodFrom: filters.appliedPeriodFrom,
    appliedPeriodTo: filters.appliedPeriodTo,
    appliedPeriodYear: filters.appliedPeriodYear,
    appliedPeriodWeek: filters.appliedPeriodWeek,
  })

  const queryConfig = useQueryConfig({
    allUnits: companyData.allUnits,
    allQuestionnaires: companyData.allQuestionnaires,
    selectedCompanyValue: companyData.selectedCompany?.value,
    hasAssignedCompany: companyData.hasAssignedCompany,
  })

  const resultsConsult = useResultsConsult({
    queryMode: queryConfig.queryMode,
    selectedUnits: queryConfig.selectedUnits,
    selectedQuestionnaires: queryConfig.selectedQuestionnaires,
    canConsult: queryConfig.canConsult,
    hasAssignedCompany: companyData.hasAssignedCompany,
    assignedCompany: companyData.assignedCompany,
    selectedCompany: companyData.selectedCompany,
    appliedPeriodFrom: filters.appliedPeriodFrom,
    appliedPeriodTo: filters.appliedPeriodTo,
    appliedPeriodYear: filters.appliedPeriodYear,
    appliedPeriodWeek: filters.appliedPeriodWeek,
    filterDivision: filters.filterDivision,
    filterZone: filters.filterZone,
    filterUser: filters.filterUser,
    filterRole: filters.filterRole,
    selectedVersionsByQuestionnaire,
    showSnackbar,
  })

  const chartData = useChartData({
    resultsData: resultsConsult.resultsData,
    selectedResultIds: resultsConsult.selectedResultIds,
    queryMode: queryConfig.queryMode,
  })

  const downloadReport = useDownloadReport({
    canConsult: queryConfig.canConsult,
    hasQueried: resultsConsult.hasQueried,
    hasAssignedCompany: companyData.hasAssignedCompany,
    assignedCompany: companyData.assignedCompany,
    selectedCompany: companyData.selectedCompany,
    selectedUnits: queryConfig.selectedUnits,
    selectedQuestionnaires: queryConfig.selectedQuestionnaires,
    appliedPeriodFrom: filters.appliedPeriodFrom,
    appliedPeriodTo: filters.appliedPeriodTo,
    appliedPeriodYear: filters.appliedPeriodYear,
    appliedPeriodWeek: filters.appliedPeriodWeek,
    filterDivision: filters.filterDivision,
    filterZone: filters.filterZone,
    filterUser: filters.filterUser,
    filterRole: filters.filterRole,
    selectedVersionsByQuestionnaire,
    appliedCriticalRange: resultsConsult.appliedCriticalRange,
    showSnackbar,
  })

  const navigation = useNavigation({
    canConsult: queryConfig.canConsult,
    queryMode: queryConfig.queryMode,
    selectedCompany: companyData.selectedCompany,
    selectedUnits: queryConfig.selectedUnits,
    selectedQuestionnaires: queryConfig.selectedQuestionnaires,
    searchUnit: queryConfig.searchUnit,
    searchQuestionnaire: queryConfig.searchQuestionnaire,
    periodFrom: filters.periodFrom,
    periodTo: filters.periodTo,
    periodWeek: filters.periodWeek,
    periodYear: filters.periodYear,
    filterDivision: filters.filterDivision,
    filterZone: filters.filterZone,
    filterUser: filters.filterUser,
    filterRole: filters.filterRole,
    appliedPeriodFrom: filters.appliedPeriodFrom,
    appliedPeriodTo: filters.appliedPeriodTo,
    appliedPeriodWeek: filters.appliedPeriodWeek,
    appliedPeriodYear: filters.appliedPeriodYear,
    hasQueried: resultsConsult.hasQueried,
    resultsData: resultsConsult.resultsData,
    performanceData: resultsConsult.performanceData,
    consultedItems: resultsConsult.consultedItems,
    selectedResultIds: resultsConsult.selectedResultIds,
    activeTab: resultsConsult.activeTab,
    chartType: resultsConsult.chartType,
    appliedProcessStatusParams: resultsConsult.appliedProcessStatusParams,
    appliedCriticalRange: resultsConsult.appliedCriticalRange,
    lastConsultTime: resultsConsult.lastConsultTime,
    pieChartData: resultsConsult.pieChartData,
    pieChartPagination: resultsConsult.pieChartPagination,
    resultsPage: resultsConsult.resultsPage,
    selectedVersionsByQuestionnaire,
    showVersionsPanel,
    activeVersionQuestionnaireId,
    setFilterDivision: filters.setFilterDivision,
    setFilterZone: filters.setFilterZone,
    setFilterUser: filters.setFilterUser,
    setFilterRole: filters.setFilterRole,
  })

  const clearVersionSelections = useCallback(() => {
    setSelectedVersionsByQuestionnaire({})
    setShowVersionsPanel(false)
    setActiveVersionQuestionnaireId(null)
  }, [])

  const openVersionsPanel = useCallback(() => {
    const questionnairesWithVersions = queryConfig.selectedQuestionnairesWithVersions
    if (questionnairesWithVersions.length === 0) return
    setShowVersionsPanel(true)
    setActiveVersionQuestionnaireId(questionnairesWithVersions[0].id)
  }, [queryConfig.selectedQuestionnairesWithVersions])

  useEffect(() => {
    if (!queryConfig.hasSelectedQuestionnaireWithVersions) {
      setShowVersionsPanel(false)
    }
  }, [queryConfig.hasSelectedQuestionnaireWithVersions])

  const autoSelectedSingleVersions = useRef<Set<string>>(new Set())

  useEffect(() => {
    const currentIds = new Set(queryConfig.selectedQuestionnaires.map((q) => q.id))

    autoSelectedSingleVersions.current = new Set(
      [...autoSelectedSingleVersions.current].filter((id) => currentIds.has(id))
    )

    setSelectedVersionsByQuestionnaire((prev) => {
      const next: Record<string, string[]> = {}

      queryConfig.selectedQuestionnaires.forEach((q) => {
        const hasSingleVersion = q.versions && q.versions.length === 1
        const alreadyAutoSelected = autoSelectedSingleVersions.current.has(q.id)

        if (hasSingleVersion && !alreadyAutoSelected) {
          next[q.id] = [String(q.versions![0].id)]
          autoSelectedSingleVersions.current.add(q.id)
        } else {
          next[q.id] = prev[q.id] || []
        }
      })

      return next
    })
  }, [queryConfig.selectedQuestionnaires])

  const setActiveVersionQuestionnaire = useCallback((id: string) => {
    setActiveVersionQuestionnaireId(id)
  }, [])

  const toggleVersionSelection = useCallback((questionnaireId: string, versionId: string) => {
    setSelectedVersionsByQuestionnaire((prev) => {
      const current = prev[questionnaireId] || []
      const isMulti = queryConfig.queryMode === 'one-unit-many-questionnaires'

      if (current.includes(versionId)) {
        return { ...prev, [questionnaireId]: current.filter((id) => id !== versionId) }
      }

      if (isMulti) {
        return { ...prev, [questionnaireId]: [...current, versionId] }
      }

      return { ...prev, [questionnaireId]: [versionId] }
    })
  }, [queryConfig.queryMode])

  const toggleAllVersionsForQuestionnaire = useCallback((questionnaireId: string) => {
    setSelectedVersionsByQuestionnaire((prev) => {
      const questionnaire = queryConfig.selectedQuestionnaires.find((q) => q.id === questionnaireId)
      if (!questionnaire || !questionnaire.versions || questionnaire.versions.length === 0) {
        return prev
      }

      const allVersionIds = questionnaire.versions.map((v) => String(v.id))
      const current = prev[questionnaireId] || []
      const allSelected = allVersionIds.every((id) => current.includes(id))

      if (allSelected) {
        return { ...prev, [questionnaireId]: [] }
      }

      return { ...prev, [questionnaireId]: allVersionIds }
    })
  }, [queryConfig.selectedQuestionnaires])

  const handleCleanFilters = useCallback(() => {
    if (!companyData.hasAssignedCompany) {
      companyData.setSelectedCompany(null)
    }
    queryConfig.clearSelections()
    resultsConsult.clearResults()
    clearVersionSelections()
    filters.handleCleanFilters()
  }, [companyData, queryConfig, resultsConsult, filters, clearVersionSelections])

  const handleQueryModeChange = useCallback((mode: QueryMode) => {
    queryConfig.handleQueryModeChange(mode)
    resultsConsult.setHasQueried(false)
    clearVersionSelections()
  }, [queryConfig, resultsConsult, clearVersionSelections])

  const effectiveQuestionnaireCount = queryConfig.selectedQuestionnaires.reduce((count, q) => {
    if (!q.versions || q.versions.length === 0) {
      return count + 1
    }
    const selectedVersions = selectedVersionsByQuestionnaire[q.id] || []
    return count + selectedVersions.length
  }, 0)

  const canViewResults = queryConfig.canConsult && effectiveQuestionnaireCount >= (
    queryConfig.queryMode === 'one-unit-many-questionnaires' ? 2 : 1
  )

  return {
    queryMode: queryConfig.queryMode,
    handleQueryModeChange,
    selectedCompany: companyData.selectedCompany,
    setSelectedCompany: companyData.setSelectedCompany,
    selectedUnits: queryConfig.selectedUnits,
    selectedQuestionnaires: queryConfig.selectedQuestionnaires,
    searchUnit: queryConfig.searchUnit,
    setSearchUnit: queryConfig.setSearchUnit,
    searchQuestionnaire: queryConfig.searchQuestionnaire,
    setSearchQuestionnaire: queryConfig.setSearchQuestionnaire,
    hasQueried: resultsConsult.hasQueried,
    filteredUnits: queryConfig.filteredUnits,
    filteredQuestionnaires: queryConfig.filteredQuestionnaires,
    canConsult: queryConfig.canConsult,
    isUnitSelected: queryConfig.isUnitSelected,
    isQuestionnaireSelected: queryConfig.isQuestionnaireSelected,
    toggleUnit: queryConfig.toggleUnit,
    toggleQuestionnaire: queryConfig.toggleQuestionnaire,
    areAllUnitsSelected: queryConfig.areAllUnitsSelected,
    areAllQuestionnairesSelected: queryConfig.areAllQuestionnairesSelected,
    toggleAllUnits: queryConfig.toggleAllUnits,
    toggleAllQuestionnaires: queryConfig.toggleAllQuestionnaires,
    unitSelectionType: queryConfig.unitSelectionType,
    questionnaireSelectionType: queryConfig.questionnaireSelectionType,
    selectedQuestionnairesWithVersions: queryConfig.selectedQuestionnairesWithVersions,
    hasSelectedQuestionnaireWithVersions: queryConfig.hasSelectedQuestionnaireWithVersions,
    showVersionsPanel,
    activeVersionQuestionnaireId,
    selectedVersionsByQuestionnaire,
    openVersionsPanel,
    setActiveVersionQuestionnaire,
    toggleVersionSelection,
    toggleAllVersionsForQuestionnaire,
    canViewResults,
    handleCleanFilters,
    handleConsult: resultsConsult.handleConsult,
    handleDownload: downloadReport.handleDownload,
    handleNavigateToProgramation: navigation.handleNavigateToProgramation,
    companiesList: companyData.companiesList,
    companiesLoading: companyData.companiesLoading,
    dataLoading: companyData.dataLoading,
    hasAssignedCompany: companyData.hasAssignedCompany,
    periodFrom: filters.periodFrom,
    setPeriodFrom: filters.setPeriodFrom,
    periodTo: filters.periodTo,
    setPeriodTo: filters.setPeriodTo,
    periodWeek: filters.periodWeek,
    setPeriodWeek: filters.setPeriodWeek,
    periodYear: filters.periodYear,
    setPeriodYear: filters.setPeriodYear,
    periodValidationError: filters.periodValidationError,
    hasPeriodErrors: filters.hasPeriodErrors,
    filterDivision: filters.filterDivision,
    setFilterDivision: filters.setFilterDivision,
    filterZone: filters.filterZone,
    setFilterZone: filters.setFilterZone,
    filterUser: filters.filterUser,
    setFilterUser: filters.setFilterUser,
    filterRole: filters.filterRole,
    setFilterRole: filters.setFilterRole,
    divisionsOptions: filters.divisionsOptions,
    zonesOptions: filters.zonesOptions,
    usersOptions: filters.usersOptions,
    rolesOptions: filters.rolesOptions,
    filtersLoading: filters.filtersLoading,
    isPartner: filters.isPartner,
    hideZone: filters.hideZone,
    handleApplyFilters: filters.handleApplyFilters,
    filterActiveCount: filters.filterActiveCount,
    displayPeriod: filters.displayPeriod,
    snackbar,
    closeSnackbar,
    resultsData: resultsConsult.resultsData,
    resultsLoading: resultsConsult.resultsLoading,
    lastConsultTimeFormatted: resultsConsult.lastConsultTimeFormatted,
    activeTab: resultsConsult.activeTab,
    handleTabChange: resultsConsult.handleTabChange,
    chartType: resultsConsult.chartType,
    setChartType: resultsConsult.setChartType,
    selectedResultIds: resultsConsult.selectedResultIds,
    toggleResultItem: resultsConsult.toggleResultItem,
    selectAllResultItems: resultsConsult.selectAllResultItems,
    searchResult: resultsConsult.searchResult,
    setSearchResult: resultsConsult.setSearchResult,
    filteredResultItems: resultsConsult.filteredResultItems,
    resultsPage: resultsConsult.resultsPage,
    setResultsPage: resultsConsult.setResultsPage,
    totalPages: resultsConsult.totalPages,
    paginatedResultItems: resultsConsult.paginatedResultItems,
    performanceData: resultsConsult.performanceData,
    chartData: chartData.chartData,
    chartSeries: chartData.chartSeries,
    globalAverageScore: resultsConsult.globalAverageScore,
    chartColors: chartData.chartColors,
    filteredPieChartData: resultsConsult.filteredPieChartData,
    pieChartLoading: resultsConsult.pieChartLoading,
    pieChartPagination: resultsConsult.pieChartPagination,
    handleChartTypeChange: resultsConsult.handleChartTypeChange,
    handlePieChartPageChange: resultsConsult.handlePieChartPageChange,
    processStatusQueryParams: resultsConsult.processStatusQueryParams,
    restoredFromNavigation: navigation.restoredFromNavigation,
    downloadPopoverOpen: downloadReport.downloadPopoverOpen,
    downloadPopoverAnchor: downloadReport.downloadPopoverAnchor,
    closeDownloadPopover: downloadReport.closeDownloadPopover,
    downloadingPdf: downloadReport.downloadingPdf,
    downloadingExcel: downloadReport.downloadingExcel,
    handleDownloadPdf: downloadReport.handleDownloadPdf,
    handleDownloadExcel: downloadReport.handleDownloadExcel,
    handleRangeApplied: resultsConsult.handleRangeApplied,
  }
}

export { useBody }
